import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import {
  forgotPasswordDefaultValues,
  forgotPasswordSchema,
  resetPasswordDefaultValues,
  resetPasswordSchema,
} from './assets/authUtil';
import {
  useResetPasswordMutation,
  useResetPasswordRequestMutation,
} from './authSlice';
import FormikInputField from '../../app/components/formikComponents/FormikInputField';
import { Col, Container, Row } from 'react-bootstrap';
import StyledText from '../../app/components/styledComponents/StyledText';
import StyledButton from '../../app/components/styledComponents/StyledButton';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import BoostFloralAvatarIcon from '../../app/components/svgIcons/BoostFloralAvatarIcon';
import LoginImage from './assets/images/LoginImage.png';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [resetPassword, { isLoadingData }] = useResetPasswordMutation();
  const [resetPasswordRequest, { isLoading }] =
    useResetPasswordRequestMutation();

  const [otpScreenVisible, setOtpScreenVisible] = useState(false);

  return (
    <div className="px-3 px-md-5 pb-3 d-flex align-items-center flex-grow-1">
      <Container className="auth-inner bg-white rounded-3 mt-3 p-3 p-md-3 px-md-4 pb-md-3">
        <Row>
          <Col xs={12}>
            <h2 className="mb-2 mb-md-4">Password Reset</h2>
          </Col>
          <StyledLeftImage xs={12} md={5}>
            <div className="d-flex flex-column gap-3 justify-content-between align-items-center my-3">
              <BoostFloralAvatarIcon />
              <StyledText
                as="h1"
                color="secondary"
                className="fw-bolder text-uppercase"
              >
                Enter your BoostFloral email address that you used to register.
                We'll send you an otp to reset password.
              </StyledText>
            </div>
          </StyledLeftImage>

          <Col xs={12} md={7}>
            {!otpScreenVisible && (
              <Formik
                initialValues={{
                  ...forgotPasswordDefaultValues,
                }}
                validationSchema={forgotPasswordSchema}
                onSubmit={async (values) => {
                  const data = await resetPasswordRequest({ ...values });
                  if (!data?.error && data?.data?.status === 'OK') {
                    toast.success('OTP sent to your mail successfully!');
                    setOtpScreenVisible(true);
                  } else {
                    toast.error(data?.data?.objectData || 'server error');
                  }
                }}
                render={() => (
                  <StyledForm>
                    <div className="mb-2 mt-3 mt-md-5">
                      <StyledText
                        as="label"
                        color="secondary"
                        htmlFor={`email`}
                        className="form-label"
                      >
                        Email *
                      </StyledText>
                      <FormikInputField
                        name={`email`}
                        id={`email`}
                        placeholder="Type in"
                      />
                    </div>
                    <StyledButton
                      type="submit"
                      className="w-100 my-3 py-2 fw-bolder"
                      loading={isLoading}
                      disabled={isLoading}
                    >
                      <div className="py-1">Submit</div>
                    </StyledButton>
                    <div className="d-flex justify-content-center flex-wrap mb-2 fw-bolder ">
                      <StyledText color="darkenText">Go back to</StyledText>
                      <StyledText
                        as={Link}
                        className="ms-1 text-decoration-none"
                        to="/login"
                        color="primary"
                      >
                        LOGIN
                      </StyledText>
                    </div>
                  </StyledForm>
                )}
              />
            )}

            {otpScreenVisible && (
              <Formik
                initialValues={{
                  ...resetPasswordDefaultValues,
                }}
                validationSchema={resetPasswordSchema}
                onSubmit={async (values) => {
                  const data = await resetPassword({ ...values });
                  if (!data?.error && data?.data?.status === 'OK') {
                    toast.success('Password reset successfully!');
                    navigate('/login');
                  } else {
                    toast.error(data?.data?.objectData || 'server error');
                  }
                }}
                render={() => (
                  <StyledForm autoComplete="off">
                    <div className="mb-2 mt-3 mt-md-5">
                      <StyledText
                        as="label"
                        color="secondary"
                        htmlFor={`OTP`}
                        className="form-label"
                      >
                        OTP *
                      </StyledText>
                      <FormikInputField
                        name="otp"
                        type="text"
                        $fontSize="16px"
                        $phFontSize="16px"
                        id="otp"
                        placeholder="Enter OTP"
                        autoComplete="one-time-code"
                      />
                    </div>

                    <div className="mb-2 mt-1 mt-md-3">
                      <StyledText
                        as="label"
                        color="secondary"
                        htmlFor={`password`}
                        className="form-label"
                      >
                        New Password *
                      </StyledText>
                      <FormikInputField
                        name="password"
                        type="password"
                        $fontSize="16px"
                        $phFontSize="16px"
                        id="password"
                        placeholder="Enter New Password"
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="mb-2 mt-1 mt-md-3">
                      <StyledText
                        as="label"
                        color="secondary"
                        htmlFor={`confirmPassword`}
                        className="form-label"
                      >
                        Confirm Password *
                      </StyledText>
                      <FormikInputField
                        name="confirmPassword"
                        type="password"
                        $fontSize="16px"
                        $phFontSize="16px"
                        id="confirmPassword"
                        placeholder="Enter New Password"
                        autoComplete="new-password"
                      />
                    </div>
                    <StyledButton
                      type="submit"
                      className="w-100 my-3 py-2 fw-bolder"
                      loading={isLoadingData}
                      disabled={isLoadingData}
                    >
                      <div className="py-1">Submit</div>
                    </StyledButton>
                    <div className="d-flex justify-content-center flex-wrap mb-2 fw-bolder ">
                      <StyledText color="darkenText">Go back to</StyledText>
                      <StyledText
                        as={Link}
                        className="ms-1 text-decoration-none"
                        to="/login"
                        color="primary"
                      >
                        LOGIN
                      </StyledText>
                    </div>
                  </StyledForm>
                )}
              />
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const StyledForm = styled(Form)`
  max-width: 100%;
  margin: 0 auto;
  @media (min-width: 720px) {
    max-width: 600px;
  }
`;

const StyledLeftImage = styled(Col)`
  background-image: url(${LoginImage});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top center;
  border-radius: 10px;
`;

export default ForgotPassword;
